import React from 'react';

import { FormButtonsContainer } from '@components/Container';
import { CancelButton, SubmitButton } from '@components/Form';
import type { SubmitType } from '@hooks/useFormSubmission';

export interface FormActionButton {
  label: string;
  submitType: SubmitType;
  show?: boolean;
}

export interface FormActionsBarProps {
  onCancel: () => void;
  isSubmitting: boolean;
  isValid: boolean;
  buttons: FormActionButton[];
  onSubmitTypeChange: (type: SubmitType) => void;
}

/**
 * Reusable form actions bar with cancel and submit buttons.
 *
 * @example
 * ```tsx
 * <FormActionsBar
 *   onCancel={() => navigate(-1)}
 *   isSubmitting={formikProps.isSubmitting}
 *   isValid={formikProps.isValid}
 *   buttons={[
 *     { label: 'Save & Close', submitType: 'saveAndClose' },
 *     { label: 'Save', submitType: 'save' },
 *   ]}
 *   onSubmitTypeChange={setSubmitType}
 * />
 * ```
 */
export const FormActionsBar: React.FC<FormActionsBarProps> = ({
  onCancel,
  isSubmitting,
  isValid,
  buttons,
  onSubmitTypeChange,
}) => {
  return (
    <div className="flex items-end justify-end h-full">
      <FormButtonsContainer>
        <CancelButton onClick={onCancel} />

        {buttons
          .filter((btn) => btn.show !== false)
          .map((btn) => (
            <SubmitButton
              key={btn.submitType}
              label={btn.label}
              disabled={isSubmitting || !isValid}
              onClick={() => onSubmitTypeChange(btn.submitType)}
            />
          ))}
      </FormButtonsContainer>
    </div>
  );
};
